import type { Data } from "@eai-toolkit/client";
import {
  EditNote,
  Refresh,
  RemoveCircle,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useMemo, useState } from "react";
import { Provider } from "react-redux";

import { AutocompleteWithWrap } from "@components";

import { AccountSelector } from "../../../components/AccountSelector/AccountSelector";
import DescriptiveTooltip from "../../../components/DescriptiveTooltip";
import ResourceSelector from "../../../components/ResourceSelector/ResourceSelector";
import {
  checkDataAccessLevel,
  type DataAccessLevel,
} from "../../../helpers/api";
import { SHARED_BOOKMARK_CACHE_IDS } from "../../../hooks/useResourceBookmarks";
// TODO: The notebooks module uses a legacy Redux store (initStore) that predates the unified RTK
// store. ResourceSelector needs the RTK middleware, so we bridge with a nested Provider here.
// The whole notebooks module needs to be migrated to the unified store.
import { store as unifiedStore } from "../../../store/store";
import { isToolkitValidName } from "../helpers";
import type { NewJobParams } from "../types/api";

const SelectorDiv = styled("div")(({ theme }) => ({
  padding: `${theme.spacing(2)} 0`,
  width: 320,
}));

const NEW_NOTEBOOK_DATASET_CACHE_ID = SHARED_BOOKMARK_CACHE_IDS.data;

const NumberInputGroup = styled("div")(({ theme }) => ({
  display: "flex",
  padding: `${theme.spacing(2)} 0`,
  width: 320,
}));

const NumberInput = styled("div")(({ theme }) => ({
  width: "50%",
  "&:nth-of-type(1)": {
    marginRight: theme.spacing(1),
  },
}));

const LoadingSpinner = styled(InputAdornment)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(3),
}));

const DatasetsPaper = styled(Paper)({
  width: 320,
});

interface ComponentProps {
  jupyterImages: string[] | null;
  newJobParams: NewJobParams;
  handleJobConfigChange: (newJobParams: NewJobParams) => void;
  updateRandomName: () => void;
  onDataAccessLevelsChange?: (dataAccessLevel: DataAccessLevel[]) => void;
}

type Props = ComponentProps;

interface TooltipState {
  [key: string]: { hover: boolean; tooltipOpen: boolean };
}

const NewNotebook = (props: Props) => {
  const {
    jupyterImages,
    newJobParams,
    handleJobConfigChange,
    updateRandomName,
    onDataAccessLevelsChange,
  } = props;

  const [selectedDataset, setSelectedDataset] = useState<Data | null>(null);
  const [datasetSelectorKey, setDatasetSelectorKey] = useState(0);

  const [dataAccessLevels, setDataAccessLevels] = useState<DataAccessLevel[]>(
    [],
  );

  useEffect(() => {
    // Expose dataAccessLevels to parent component when it changes
    if (onDataAccessLevelsChange) {
      onDataAccessLevelsChange(dataAccessLevels);
    }
  }, [dataAccessLevels, onDataAccessLevelsChange]);

  const [tooltipState, setTooltipState] = useState<TooltipState>({}); // Map to track tooltip state for all datasets: [data id] -> state

  const handleChange =
    (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      const newJobConfig = { ...newJobParams, [key]: newValue };
      handleJobConfigChange(newJobConfig);
    };

  const handleNumberChange =
    (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      const newJobConfig = {
        ...newJobParams,
        [key]: newValue ? Number(newValue) : null,
      };
      handleJobConfigChange(newJobConfig);
    };

  const handleAccountSelected = (accountId: string | null) => {
    handleJobConfigChange({ ...newJobParams, accountId: accountId ?? "" });
  };

  const handleImageSelected = (id: string | null) => {
    const jobParams: NewJobParams = {
      ...newJobParams,
      imageId: id ?? "",
    };
    handleJobConfigChange(jobParams);
  };

  const checkDataAccess = async (data: Data) => {
    const result = await checkDataAccessLevel(data.urn);
    setDataAccessLevels((prev) => [
      ...prev.filter((item) => item.urn !== data.urn),
      result,
    ]);
  };

  const handleDataSelected = async (d: Data | null) => {
    if (d && !newJobParams.sourceData?.some((data) => data.id === d.id)) {
      const jobParams: NewJobParams = {
        ...newJobParams,
        sourceData: (newJobParams.sourceData ?? []).concat(d),
      };
      handleJobConfigChange(jobParams);
      checkDataAccess(d).catch((err) => {
        console.error("Data access check failed", err, { urn: d.urn });
      });
      // Reset the selector and refocus it for the next entry only when a
      // dataset was actually added — skip on duplicates to avoid a needless
      // remount and focus steal.
      setSelectedDataset(null);
      setDatasetSelectorKey((k) => k + 1);
    }
  };

  const handleDataRemoved = (d: Data) => {
    const jobParams: NewJobParams = {
      ...newJobParams,
      sourceData: (newJobParams.sourceData ?? []).filter(
        (data) => data.id !== d.id,
      ),
    };
    handleJobConfigChange(jobParams);
  };

  const handleUITypeSelected = (id: "lab" | "notebook") => {
    const jobParams: NewJobParams = {
      ...newJobParams,
      useLab: id === "lab" ? true : false,
    };
    handleJobConfigChange(jobParams);
  };

  const nameIsInvalid = newJobParams.jobName
    ? isToolkitValidName(newJobParams.jobName)
    : false;

  const imageOptions = useMemo(
    () =>
      jupyterImages?.map((image) => ({
        id: image,
        name: image,
      })) ?? null,
    [jupyterImages],
  );

  const useLabOptions = useMemo(
    () => [
      { id: "lab", name: "jupyter lab" },
      { id: "notebook", name: "jupyter notebook (classic)" },
    ],
    [],
  );

  const loadingAutocompleteInputProps = useMemo(
    () => ({
      endAdornment: (
        <LoadingSpinner position="end">
          <CircularProgress color="inherit" size={20} />
        </LoadingSpinner>
      ),
    }),
    [],
  );

  useEffect(() => {
    if (!newJobParams.sourceData) return;

    const newTooltipState: TooltipState = {};
    newJobParams.sourceData.forEach((data) => {
      const accessLevel = dataAccessLevels.find(
        (level) => level.urn === data.urn,
      );
      if (accessLevel && !accessLevel.hasWriteAccess) {
        newTooltipState[data.id] = { hover: false, tooltipOpen: true };
        setTimeout(() => {
          setTooltipState((prev) => {
            const updatedState = { ...prev };
            if (updatedState[data.id]) {
              updatedState[data.id].tooltipOpen = false;
            }
            return updatedState;
          });
        }, 1000);
      } else {
        newTooltipState[data.id] = { hover: false, tooltipOpen: false };
      }
    });
    setTooltipState(newTooltipState);
  }, [newJobParams.sourceData, dataAccessLevels]);

  return (
    <>
      <SelectorDiv>
        <Provider store={unifiedStore}>
          <AccountSelector
            value={newJobParams.accountId || null}
            onChange={handleAccountSelected}
            required
            sx={{ width: "100%" }}
          />
        </Provider>
      </SelectorDiv>

      <SelectorDiv>
        <AutocompleteWithWrap
          getOptionLabel={(option) => option.name || option.id}
          options={imageOptions ?? []}
          value={
            imageOptions?.find((o) => o.id === newJobParams.imageId) ?? null
          }
          onChange={(_, value) => {
            handleImageSelected(value?.id ?? null);
          }}
          renderInput={(params) => (
            <TextField
              required={true}
              {...params}
              label="Docker image"
              variant="outlined"
              InputProps={
                imageOptions
                  ? params.InputProps
                  : {
                      ...params.InputProps,
                      ...loadingAutocompleteInputProps,
                    }
              }
            />
          )}
        />
      </SelectorDiv>

      <SelectorDiv>
        <AutocompleteWithWrap
          getOptionLabel={(option) => option.name || option.id}
          options={useLabOptions}
          value={
            useLabOptions.find(
              (o) => o.id === (newJobParams.useLab ? "lab" : "notebook"),
            ) ?? undefined
          }
          onChange={(_, value) => {
            handleUITypeSelected(value!.id as "lab" | "notebook");
          }}
          disableClearable={true}
          renderInput={(params) => (
            <TextField
              required={true}
              {...params}
              label="UI version"
              variant="outlined"
            />
          )}
        />
      </SelectorDiv>

      <NumberInputGroup>
        <NumberInput>
          <TextField
            variant="outlined"
            fullWidth
            label="CPUs"
            type="number"
            required
            error={newJobParams.cpu === 0}
            inputProps={{
              min: 0,
            }}
            onChange={handleNumberChange("cpu")}
            value={newJobParams.cpu}
          />
        </NumberInput>

        <NumberInput>
          <TextField
            variant="outlined"
            fullWidth
            label="RAM (GB)"
            type="number"
            required
            inputProps={{
              min: 1,
              step: 1,
            }}
            onChange={handleNumberChange("mem")}
            value={newJobParams.mem}
          />
        </NumberInput>
      </NumberInputGroup>

      <NumberInputGroup>
        <NumberInput>
          <TextField
            variant="outlined"
            fullWidth
            label="GPUs"
            type="number"
            required
            inputProps={{
              min: 0,
              step: 1,
            }}
            onChange={handleNumberChange("gpu")}
            value={newJobParams.gpu}
          />
        </NumberInput>

        <NumberInput>
          <TextField
            variant="outlined"
            fullWidth
            label="GPU memory (GB)"
            type="number"
            disabled={!newJobParams.gpu}
            inputProps={{
              min: 12,
              max: 32,
              step: 1,
            }}
            placeholder="don't care"
            InputLabelProps={{ shrink: true }}
            onChange={handleNumberChange("gpuMem")}
            value={newJobParams.gpuMem ?? ""}
          />
        </NumberInput>
      </NumberInputGroup>

      <SelectorDiv>
        <TextField
          variant="outlined"
          fullWidth
          error={nameIsInvalid}
          label="Name"
          helperText={nameIsInvalid ? "Alphanumeric and underscores only." : ""}
          onChange={handleChange("jobName")}
          value={newJobParams.jobName}
          placeholder={newJobParams.randomJobName}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <IconButton
                onClick={updateRandomName}
                title="New random name"
                disabled={Boolean(newJobParams.jobName)}
              >
                <Refresh />
              </IconButton>
            ),
          }}
        />
      </SelectorDiv>

      <SelectorDiv>
        <Provider store={unifiedStore}>
          <ResourceSelector<Data>
            key={datasetSelectorKey}
            resourceType="data"
            cacheId={NEW_NOTEBOOK_DATASET_CACHE_ID}
            value={selectedDataset}
            onChange={handleDataSelected}
            label="Add a dataset"
            placeholder="Search for a dataset..."
            required={!newJobParams.sourceData?.length}
            autoFocus={datasetSelectorKey > 0}
          />
        </Provider>
      </SelectorDiv>

      {newJobParams.sourceData?.length ? (
        <>
          <Typography variant="h4">Datasets</Typography>
          <DatasetsPaper>
            <List>
              {newJobParams.sourceData.map((data) => {
                const accessLevel = dataAccessLevels.find(
                  (level) => level.urn === data.urn,
                );

                const handleMouseEnter = () => {
                  setTooltipState((prev) => ({
                    ...prev,
                    [data.id]: { ...prev[data.id], hover: true },
                  }));
                };

                const handleMouseLeave = () => {
                  setTooltipState((prev) => ({
                    ...prev,
                    [data.id]: { ...prev[data.id], hover: false },
                  }));
                };

                return (
                  <ListItem key={data.id}>
                    <ListItemText>
                      <Box
                        sx={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <DescriptiveTooltip
                          title={
                            accessLevel?.hasWriteAccess
                              ? "Read/Write Access"
                              : "Read-Only Access"
                          }
                          open={
                            tooltipState[data.id]?.tooltipOpen ||
                            tooltipState[data.id]?.hover ||
                            false
                          }
                          onMouseEnter={handleMouseEnter}
                          onMouseLeave={handleMouseLeave}
                          placement="right"
                        >
                          <Box sx={{ marginRight: 1, display: "flex" }}>
                            {accessLevel ? (
                              accessLevel.hasWriteAccess ? (
                                <EditNote color="primary" fontSize="small" />
                              ) : (
                                <VisibilityOutlined
                                  color="primary"
                                  fontSize="small"
                                />
                              )
                            ) : (
                              <CircularProgress size={16} />
                            )}
                          </Box>
                        </DescriptiveTooltip>
                        {data.fullName || data.id}
                      </Box>
                    </ListItemText>
                    <ListItemSecondaryAction>
                      <IconButton onClick={() => handleDataRemoved(data)}>
                        <RemoveCircle />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </DatasetsPaper>
        </>
      ) : null}
    </>
  );
};

export default NewNotebook;
